import { useEffect } from 'react';

import { GeolocationPermissionStates } from 'utils/geolocation';

import { UseUpdateOnLocationPermissionChange } from './types';

const browserPermissionToGeolocationPermission: Record<
  PermissionStatus['state'],
  GeolocationPermissionStates
> = {
  granted: GeolocationPermissionStates.GRANTED,
  denied: GeolocationPermissionStates.DENIED,
  prompt: GeolocationPermissionStates.PROMPT,
};

/**
 * Notifies when the user changes the browser's location permission, e.g. allowing it after the
 * getCurrentPosition request has already timed out, or toggling it from the site settings.
 * The tab never loses visibility in these cases, so the app state listener doesn't catch them.
 */
export const useUpdateOnLocationPermissionChange: UseUpdateOnLocationPermissionChange =
  onLocationPermissionChange => {
    useEffect(() => {
      if (!navigator.permissions?.query) {
        return;
      }

      let isSubscribed = true;
      let permissionStatus: PermissionStatus | null = null;

      const handleChange = () => {
        if (permissionStatus) {
          onLocationPermissionChange(
            browserPermissionToGeolocationPermission[permissionStatus.state]
          );
        }
      };

      navigator.permissions
        .query({ name: 'geolocation' })
        .then(status => {
          if (!isSubscribed) {
            return;
          }
          permissionStatus = status;
          permissionStatus.addEventListener('change', handleChange);
        })
        .catch(() => {
          // Permissions API unsupported for geolocation in this browser
        });

      return () => {
        isSubscribed = false;
        permissionStatus?.removeEventListener('change', handleChange);
      };
    }, [onLocationPermissionChange]);
  };
